import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelect, MatSelectModule } from '@angular/material/select';
import { ConfigService } from '../../shared-services/config.service';
import { GlobalService } from '../../shared-services/global.service';
import { map, catchError, of, forkJoin, Subscription } from 'rxjs';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { DateRangeFilterComponent, DateRangeValue } from '../date-range-filter/date-range-filter.component';

@Component({
  selector: 'app-custom-filter',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    FormsModule,
    NgxMatSelectSearchModule,
    DateRangeFilterComponent,
  ],
  templateUrl: './custom-filter.component.html',
  styleUrl: './custom-filter.component.scss',
})
export class CustomFilterComponent {
  @Input() filters!: any;
  @Input() layout: 'row' | 'column' = 'row';
  @Output() filtersChanged = new EventEmitter<any>();
  @ViewChildren(MatSelect) private dropdowns!: QueryList<MatSelect>;
  private readonly productRoadmapBodyClass = 'product-roadmap-scroll-unlock';
  private shouldManageRoadmapBodyClass = false;

  selectedValues: any = {};
  private searchControls: Record<string, FormControl<string>> = {};
  private resetSub!: Subscription;
  private ganttOptionsSub!: Subscription;
  fromDateFilter: any = null;
  toDateFilter: any = null;
  fromDateValue: Date | null = null;
  toDateValue: Date | null = null;

  constructor(
    private configService: ConfigService,
    private filterState: GlobalService
  ) { }

  private shouldMergeDuplicateSelectionKey(key: string): boolean {
    const mergeConfig = this.filters?.mergeDuplicateSelectionValues;

    if (Array.isArray(mergeConfig)) {
      return mergeConfig.includes(key);
    }

    return mergeConfig === true;
  }

  private shouldCombineQuarterYearRange(): boolean {
    return this.filters?.combineQuarterYearRange === true;
  }

  private getYearMonthCombineConfig(): any {
    const config = this.filters?.combineYearMonthSelection;
    return config && typeof config === 'object' ? config : null;
  }

  private getDatePartValue(value: any, part: 'year' | 'month'): string {
    const raw = String(value ?? '').trim();
    const match = raw.match(/^(\d{4})-(\d{1,2})/);
    if (!match) return '';

    return part === 'year' ? match[1] : match[2].padStart(2, '0');
  }

  private getYearMonthValue(value: any): string {
    const raw = String(value ?? '').trim();
    const match = raw.match(/^(\d{4})-(\d{1,2})/);
    if (!match) return '';

    return `${match[1]}-${match[2].padStart(2, '0')}`;
  }

  private getMonthLabel(month: string): string {
    const monthNumber = Number(month);
    if (!Number.isInteger(monthNumber) || monthNumber < 1 || monthNumber > 12) {
      return month;
    }

    return new Date(2000, monthNumber - 1, 1).toLocaleString('en-US', {
      month: 'long',
    });
  }

  private getYearMonthLabel(yearMonth: string): string {
    const [year, month] = yearMonth.split('-');
    if (!year || !month) return yearMonth;

    return `${this.getMonthLabel(month)} ${year}`;
  }

  private extractQuarterYearValues(selection: any): string[] {
    if (selection === null || selection === undefined || selection === '') {
      return [];
    }

    const items = Array.isArray(selection) ? selection : [selection];
    const values: string[] = [];

    items.forEach((item: any) => {
      if (item === null || item === undefined || item === '' || item === 'All') {
        return;
      }

      if (typeof item === 'object') {
        const quarterYear = item?.quarter_year ?? item?.quarterYear ?? item?.value ?? item?.id ?? item?.name;
        if (Array.isArray(quarterYear)) {
          quarterYear.forEach((entry: any) => {
            if (entry !== null && entry !== undefined && String(entry).trim() !== '') {
              values.push(String(entry).trim());
            }
          });
        } else if (quarterYear !== null && quarterYear !== undefined && String(quarterYear).trim() !== '') {
          values.push(String(quarterYear).trim());
        }
        return;
      }

      values.push(String(item).trim());
    });

    return values.filter((value) => value !== '');
  }

  private setFormattedSelectionValue(
    formattedSelections: any,
    key: string,
    values: any[]
  ): void {
    if (!this.shouldMergeDuplicateSelectionKey(key)) {
      formattedSelections[key] = values;
      return;
    }

    const existing = Array.isArray(formattedSelections[key])
      ? formattedSelections[key]
      : [];
    formattedSelections[key] = Array.from(new Set([...existing, ...values]));
  }

  ngOnInit(): void {
    if (!this.filters?.content) return;
    this.initializeDateRangeFilters();
    this.setDefaultDateRangeToToday();
    this.shouldManageRoadmapBodyClass =
      typeof document !== 'undefined' &&
      document.body.classList.contains(this.productRoadmapBodyClass);

    this.resetSub = this.filterState.reset$.subscribe(() => {
      this.resetSelection();
    });

    this.ganttOptionsSub = this.configService.ganttDropdownOptions$.subscribe((payload) => {
      this.applyGanttDropdownOptions(payload);
    });

    const apiObservables: any[] = [];
    const staticFilters: any[] = [];
    const sharedApiGroups = new Map<string, any[]>();

    this.filters.content.forEach((filter: any) => {
      if (filter.optionsSource === 'gantt') {
        filter.optionsList = [];
        staticFilters.push(filter);
        return;
      }

      if (filter.apiUrl && !this.getDependencyKeys(filter).length) {
        if (filter.sharedDropdownRequestKey) {
          const key = String(filter.sharedDropdownRequestKey);
          if (!sharedApiGroups.has(key)) {
            sharedApiGroups.set(key, []);
          }
          sharedApiGroups.get(key)?.push(filter);
          return;
        }

        const obs = this.configService

          .fetchData(filter.apiUrl, filter.functionName, {}, filter, filter.method || 'post')

          .pipe(
            map((res: any) => {
              filter.optionsList = this.sortOptionsForFilter(
                filter,
                this.extractOptionsFromResponse(filter, res),
              );
              return [filter];
            }),
            catchError((err) => {
              console.error(`Error fetching ${filter.key} options:`, err);
              filter.optionsList = [];
              return of([filter]);
            })
          );
        apiObservables.push(obs);
      } else {
        filter.optionsList = this.sortOptionsForFilter(filter, filter.data);
        staticFilters.push(filter);
      }
    });

    sharedApiGroups.forEach((groupFilters) => {
      if (!groupFilters.length) return;
      const sourceFilter = groupFilters[0];
      const obs = this.configService
        .fetchData(
          sourceFilter.apiUrl,
          sourceFilter.functionName,
          {},
          sourceFilter,
          sourceFilter.method || 'post'
        )
        .pipe(
          map((res: any) => {
            groupFilters.forEach((filter: any) => {
              filter.optionsList = this.sortOptionsForFilter(
                filter,
                this.extractOptionsFromResponse(filter, res),
              );
            });
            return groupFilters;
          }),
          catchError((err) => {
            console.error(`Error fetching shared dropdown options:`, err);
            groupFilters.forEach((filter: any) => {
              filter.optionsList = [];
            });
            return of(groupFilters);
          })
        );
      apiObservables.push(obs);
    });

    // Set defaults for static filters first
    staticFilters.forEach((f) => this.setDefaultSelection(f, false));

    if (apiObservables.length) {
      // Wait for all API filters to load, then set defaults and emit once
      forkJoin(apiObservables).subscribe((apiFilterGroups) => {
        const apiFilters = apiFilterGroups.flat();
        apiFilters.forEach((f: any) => this.setDefaultSelection(f, false));
        this.emitSelections(); // emit only once after all defaults are set
      });
    } else {
      // No API filters, emit immediately
      this.emitSelections();
    }
  }

  private getDependencyKeys(filter: any): string[] {
    const rawDependencies = Array.isArray(filter?.dependsOn)
      ? filter.dependsOn
      : filter?.dependsOn
        ? [filter.dependsOn]
        : Array.isArray(filter?.requestFilterKeys)
          ? filter.requestFilterKeys
          : [];

    return rawDependencies
      .map((value: any) => String(value || '').trim())
      .filter((value: string) => !!value);
  }

  private shouldReloadDependentFilter(filter: any, changedKey: string): boolean {
    return this.getDependencyKeys(filter).includes(changedKey);
  }

  private hasResolvedDependencies(filter: any): boolean {
    const dependencyKeys = this.getDependencyKeys(filter);
    if (!dependencyKeys.length) {
      return true;
    }

    return dependencyKeys.every((key) => {
      const selected = this.selectedValues[key];
      if (selected === null || selected === undefined || selected === '') {
        return false;
      }

      if (Array.isArray(selected)) {
        return selected.length > 0;
      }

      return true;
    });
  }

  private flattenValues(value: any): any[] {
    if (Array.isArray(value)) {
      return value.flatMap((item) => this.flattenValues(item));
    }

    if (value === null || value === undefined || value === '') {
      return [];
    }

    return [value];
  }

  private buildDependentRequestFilter(filter: any): Record<string, any> | null {
    const dependencyKeys = this.getDependencyKeys(filter);
    if (!dependencyKeys.length) {
      return null;
    }

    const requestFilter: Record<string, any> = {};
    const requestDataKeys = new Set(
      (Array.isArray(filter?.requestDataKeys) ? filter.requestDataKeys : [])
        .map((key: any) => String(key || '').trim())
        .filter((key: string) => !!key)
    );

    dependencyKeys.forEach((key) => {
      if (requestDataKeys.has(key)) {
        return;
      }

      const sourceFilter = this.filters?.content?.find((item: any) => item?.key === key);
      const currentSelection = this.selectedValues[key];

      if (currentSelection === null || currentSelection === undefined || currentSelection === '') {
        return;
      }

      const normalizedSelection = sourceFilter
        ? this.expandAllIfNeeded(sourceFilter)
        : this.normalizeSelectedValues(currentSelection);

      const values = this.flattenValues(normalizedSelection)
        .map((value: any) => String(value).trim())
        .filter((value: string) => !!value && value !== 'All');

      if (values.length > 0) {
        const normalizedValues =
          key === 'cleaned_vertical'
            ? values.map((value: string) =>
                value.toLowerCase() === 'o&g' ? 'OIL & GAS' : value.toUpperCase(),
              )
            : values;

        requestFilter[key] = [normalizedValues];
      }
    });

    return Object.keys(requestFilter).length ? requestFilter : null;
  }

  private initializeDateRangeFilters(): void {
    const content = Array.isArray(this.filters?.content) ? this.filters.content : [];
    this.fromDateFilter = content.find((f: any) => this.isFromDateFilter(f)) || null;
    this.toDateFilter = content.find((f: any) => this.isToDateFilter(f)) || null;
  }

  private setDefaultDateRangeToToday(): void {
    if (!this.fromDateFilter || !this.toDateFilter) {
      return;
    }

    const today = new Date();
    const todayIso = this.toIsoLocalDate(today);
    const fromKey = this.resolveDateFilterKey(this.fromDateFilter, 'from_date');
    const toKey = this.resolveDateFilterKey(this.toDateFilter, 'to_date');

    this.fromDateValue = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.toDateValue = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    this.selectedValues[fromKey] = [todayIso];
    this.selectedValues[toKey] = [todayIso];
  }

  private toIsoLocalDate(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private isFromDateFilter(filter: any): boolean {
    return String(filter?.name || '').trim().toLowerCase() === 'from date';
  }

  private isToDateFilter(filter: any): boolean {
    return String(filter?.name || '').trim().toLowerCase() === 'to date';
  }

  private isDateRangeFilter(filter: any): boolean {
    return this.isFromDateFilter(filter) || this.isToDateFilter(filter);
  }

  shouldShowAsDropdown(filter: any): boolean {
    return !this.isDateRangeFilter(filter);
  }

  private resolveDateFilterKey(filter: any, fallback: 'from_date' | 'to_date'): string {
    const explicitKey = String(filter?.key || '').trim();
    if (explicitKey) {
      return explicitKey;
    }
    return fallback;
  }

  onDateRangeChange(value: DateRangeValue): void {
    if (!this.fromDateFilter || !this.toDateFilter) {
      return;
    }

    this.fromDateValue = value.fromDate ? new Date(value.fromDate) : null;
    this.toDateValue = value.toDate ? new Date(value.toDate) : null;

    const fromKey = this.resolveDateFilterKey(this.fromDateFilter, 'from_date');
    const toKey = this.resolveDateFilterKey(this.toDateFilter, 'to_date');

    this.selectedValues[fromKey] = value.fromDate ? [value.fromDate] : [];
    this.selectedValues[toKey] = value.toDate ? [value.toDate] : [];

    // Force immediate global filter propagation for date range changes.
    this.filterState.updateFilterSelection(
      {
        [fromKey]: this.selectedValues[fromKey],
        [toKey]: this.selectedValues[toKey],
      },
      ''
    );
    this.emitSelections();
  }

  private extractOptionsFromResponse(filter: any, response: any): any {
    if (String(filter?.functionName || '') === 'UserEmailDropdown') {
      const root = response?.responseList?.[0]?.responseData?.[0];
      const candidate =
        root?.updated_by ??
        root?.user_email ??
        root?.user_emails ??
        root?.emails ??
        response?.responseList?.[0]?.responseData ??
        response?.responseData ??
        response;

      if (Array.isArray(candidate)) {
        return candidate;
      }
    }

    if (filter?.responsePath) {
      const resolved = this.byPath(response, String(filter.responsePath));
      if (filter?.respKey && resolved && typeof resolved === 'object' && !Array.isArray(resolved)) {
        return this.mapOptionValues(filter, resolved?.[filter.respKey]);
      }
      return this.mapOptionValues(filter, resolved);
    }

    if (!filter?.respKey) {
      return this.mapOptionValues(filter, response);
    }
    return this.mapOptionValues(
      filter,
      response?.responseList?.[0]?.responseData?.[0]?.[filter.respKey]
    );
  }

  private mapOptionValues(filter: any, options: any): any {
    const dateOptionPart = String(filter?.dateOptionPart || '').trim().toLowerCase();
    if (dateOptionPart === 'yearmonth' && Array.isArray(options)) {
      const optionValueKey = String(filter?.optionValueKey || 'value').trim();
      const optionRequestKey = String(filter?.optionRequestKey || filter?.key || 'value').trim();
      const values = options
        .map((option: any) => {
          const sourceValue =
            option && typeof option === 'object'
              ? option[optionValueKey] ?? option.value ?? option.id ?? option.name
              : option;
          return this.getYearMonthValue(sourceValue);
        })
        .filter((value: string) => !!value);

      return Array.from(new Set(values))
        .sort()
        .map((value) => ({
          viewValue: this.getYearMonthLabel(value),
          [optionRequestKey]: value,
        }));
    }

    if ((dateOptionPart === 'year' || dateOptionPart === 'month') && Array.isArray(options)) {
      const optionValueKey = String(filter?.optionValueKey || 'value').trim();
      const values = options
        .map((option: any) => {
          const sourceValue =
            option && typeof option === 'object'
              ? option[optionValueKey] ?? option.value ?? option.id ?? option.name
              : option;
          return this.getDatePartValue(sourceValue, dateOptionPart as 'year' | 'month');
        })
        .filter((value: string) => !!value);

      return Array.from(new Set(values))
        .sort()
        .map((value) =>
          dateOptionPart === 'month'
            ? { viewValue: this.getMonthLabel(value), value }
            : value
        );
    }

    const optionValueKey = String(filter?.optionValueKey || '').trim();
    if (!optionValueKey || !Array.isArray(options)) {
      return options;
    }

    const optionLabelKey = String(filter?.optionLabelKey || '').trim();
    const optionRequestKey = String(filter?.optionRequestKey || '').trim();
    if (optionLabelKey && optionRequestKey) {
      return options
        .map((option: any) =>
          option && typeof option === 'object'
            ? {
                viewValue: option[optionLabelKey],
                [optionRequestKey]: option[optionValueKey],
              }
            : option
        )
        .filter(
          (option: any) =>
            option !== null &&
            option !== undefined &&
            (typeof option !== 'object' ||
              option[optionRequestKey] !== null &&
              option[optionRequestKey] !== undefined)
        );
    }

    return options
      .map((option: any) =>
        option && typeof option === 'object'
          ? option[optionValueKey]
          : option
      )
      .filter((option: any) => option !== null && option !== undefined);
  }

  private sortOptionsForFilter(filter: any, options: any): any {
    if (!Array.isArray(options)) {
      return options;
    }

    const order = Array.isArray(filter?.sortOrder)
      ? filter.sortOrder
          .map((value: any) => String(value || '').trim())
          .filter((value: string) => !!value)
      : [];

    if (!order.length) {
      return options;
    }

    const orderLookup = new Map(
      order.map((value: string, index: number) => [this.normalizeSortLabel(value), index]),
    );

    return [...options]
      .map((option: any, index: number) => ({
        option,
        index,
        label: this.normalizeSortLabel(
          String(option?.viewValue ?? option?.label ?? option?.name ?? option ?? ''),
        ),
      }))
      .sort((a, b) => {
        const rankA = orderLookup.get(a.label) ?? Number.MAX_SAFE_INTEGER;
        const rankB = orderLookup.get(b.label) ?? Number.MAX_SAFE_INTEGER;

        if (rankA !== rankB) {
          return Number(rankA) - Number(rankB);
        }

        return Number(a.index) - Number(b.index);
      })
      .map((entry) => entry.option);
  }

  private normalizeSortLabel(value: string): string {
    const normalized = String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[^\w]+/g, '');

    if (!normalized) {
      return '';
    }

    if (normalized === 'oilandgas' || normalized === 'oandg' || normalized === 'og') {
      return 'og';
    }

    if (normalized === 'defence') {
      return 'defense';
    }

    return normalized;
  }

  private byPath(source: any, path: string): any {
    return String(path)
      .split('.')
      .reduce(
        (current: any, key: string) =>
          current == null ? undefined : /^\d+$/.test(key) ? current[Number(key)] : current[key],
        source,
      );
  }
  ngOnDestroy() {
    this.resetSub?.unsubscribe();
    this.ganttOptionsSub?.unsubscribe();
    this.restoreRoadmapBodyClass();
  }

  private applyGanttDropdownOptions(payload: any): void {
    if (!this.filters?.content || !payload) {
      return;
    }

    const options = Array.isArray(payload?.values) ? payload.values : [];
    const sourceKey = payload?.filterKey ? String(payload.filterKey) : '';

    this.filters.content.forEach((filter: any) => {
      if (filter?.optionsSource !== 'gantt') {
        return;
      }
      if (sourceKey && filter?.key !== sourceKey) {
        return;
      }

      const nextOptions = Array.from(
        new Set(
          options
            .map((option: any) => (option == null ? '' : String(option).trim()))
            .filter((option: string) => !!option),
        ),
      );

      filter.optionsList = nextOptions;

      const currentSelection = this.selectedValues[filter.key];
      if (!Array.isArray(currentSelection)) {
        return;
      }

      if (currentSelection.includes('All')) {
        this.emitSelections();
        return;
      }

      const filteredSelection = currentSelection.filter((value: any) =>
        nextOptions.includes(String(value).trim()),
      );

      if (filteredSelection.length !== currentSelection.length) {
        this.selectedValues[filter.key] = filteredSelection.length
          ? filteredSelection
          : ['All'];
        this.emitSelections();
      }
    });
  }

  private getExpandedParentValue(filterKey: string): any[] {
    const selected = this.selectedValues[filterKey];

    // If NOT All → return as-is
    if (!Array.isArray(selected) || !selected.includes('All')) {
      return Array.isArray(selected) ? selected : [selected];
    }

    // Expand "All" to all option values
    const filter = this.filters.content.find((f: any) => f.key === filterKey);
    if (!filter || !filter.optionsList) return [];

    return filter.optionsList.map((opt: any) =>
      typeof opt === 'object'
        ? opt.value ?? opt.id ?? opt.name ?? opt
        : opt
    );
  }

  public setDefaultSelection(filter: any, emit = true): void {
    if (this.isDateRangeFilter(filter)) {
      return;
    }
    // -------------------------------
    // APPLY DEFAULT SELECTION
    // -------------------------------
    if (filter.isMultiSelect) {
      if (filter.defaultValue) {
        const defaultValues = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [filter.defaultValue];
        this.selectedValues[filter.key] = defaultValues.map((value: any) =>
          this.resolveDefaultOptionValue(filter, value)
        );
      } else {
        this.selectedValues[filter.key] = ['All'];
      }
    } else {
      if (filter.defaultValue) {
        this.selectedValues[filter.key] = this.resolveDefaultOptionValue(
          filter,
          filter.defaultValue
        );
      } else if (this.shouldUseLatestAvailableQuarterRangeDefault(filter)) {
        this.selectedValues[filter.key] =
          this.resolveLatestAvailableQuarterRangeDefault(filter) ?? filter.optionsList?.[0] ?? null;
      } else if (filter.key === 'end_date') {
        this.selectedValues[filter.key] =
          filter.optionsList?.[filter.optionsList.length - 1] ?? null;
      } else {
        this.selectedValues[filter.key] =
          filter.optionsList?.[0] ?? null;
      }
    }

    // -------------------------------
    // TRIGGER DEPENDENT FILTERS
    // -------------------------------
    this.filters.content.forEach((f: any) => {
      if (this.shouldReloadDependentFilter(f, filter.key)) {
        const expandedParentValues = this.getExpandedParentValue(filter.key);

        const parentValueObj = {
          [filter.key]: expandedParentValues,
        };

        this.loadDependentFilter(f, parentValueObj);
      }
    });

    // -------------------------------
    // DATE VALIDATION
    // -------------------------------
    if (filter.key === 'start_date' || filter.key === 'end_date') {
      const startVal = this.selectedValues['start_date'];
      const endVal = this.selectedValues['end_date'];
      if (startVal && endVal) this.validateDateRange();
    }

    if (filter.key === 'start_quarter' || filter.key === 'end+quarter') {
      this.validateQuarterRange();
    }

    if (emit) this.emitSelections();
  }

  private shouldUseLatestAvailableQuarterRangeDefault(filter: any): boolean {
    return (
      this.filters?.defaultToLatestAvailableQuarterRange === true &&
      (filter?.key === 'start_quarter' || filter?.key === 'end+quarter')
    );
  }

  private resolveLatestAvailableQuarterRangeDefault(filter: any): any | null {
    const startFilter = this.filters?.content?.find((f: any) => f?.key === 'start_quarter');
    const endFilter = this.filters?.content?.find((f: any) => f?.key === 'end+quarter');
    const startOptions = this.getSortedQuarterOptions(
      Array.isArray(startFilter?.optionsList) ? startFilter.optionsList : []
    );
    const endOptions = this.getSortedQuarterOptions(
      Array.isArray(endFilter?.optionsList) ? endFilter.optionsList : []
    );

    if (!startOptions.length || !endOptions.length) {
      return null;
    }

    const endOption = endOptions[endOptions.length - 1];
    const startOption =
      [...startOptions]
        .reverse()
        .find((option) => option.comparable < endOption.comparable) ??
      startOptions[startOptions.length - 1];

    return filter.key === 'start_quarter' ? startOption.option : endOption.option;
  }

  private getSortedQuarterOptions(options: any[]): Array<{ option: any; comparable: number }> {
    return options
      .map((option: any) => ({
        option,
        comparable: this.getQuarterComparableValue(option),
      }))
      .filter((item) => item.comparable >= 0)
      .sort((a, b) => a.comparable - b.comparable);
  }

  private resolveDefaultOptionValue(filter: any, defaultValue: any): any {
    const options = Array.isArray(filter?.optionsList) ? filter.optionsList : [];
    if (!options.length) return defaultValue;

    const normalizedDefault = this.getOptionComparableValue(defaultValue, filter?.key);
    const matchedOption = options.find((option: any) => {
      const optionComparable = this.getOptionComparableValue(option, filter?.key);
      return optionComparable === normalizedDefault;
    });

    return matchedOption ?? defaultValue;
  }

  private getOptionComparableValue(option: any, filterKey?: string): string {
    if (option == null) return '';
    if (typeof option !== 'object') return String(option).trim().toLowerCase();

    const candidateKeys = [
      filterKey,
      'value',
      'id',
      'name',
      'viewValue',
    ].filter(Boolean) as string[];

    for (const key of candidateKeys) {
      if (option[key] != null && option[key] !== '') {
        return String(option[key]).trim().toLowerCase();
      }
    }

    return JSON.stringify(option).trim().toLowerCase();
  }



  public isEndDateOptionDisabled(option: string): boolean {
    const startValue = this.selectedValues['start_date'];
    if (!startValue) return false;

    const startFilter = this.filters.content.find((f: { key: string; }) => f.key === 'start_date');
    const endFilter = this.filters.content.find((f: { key: string; }) => f.key === 'end_date');

    if (!startFilter || !endFilter) return false;

    const startComparable = this.getComparableValue(
      startValue,
      startFilter.valueType || startFilter.type
    );
    const optionComparable = this.getComparableValue(
      option,
      endFilter.valueType || endFilter.type
    );

    // Disable if option <= selected start
    return optionComparable <= startComparable;
  }

  public isEndQuarterOptionDisabled(option: any): boolean {
    const selectedStartQuarter = this.selectedValues['start_quarter'];
    if (!selectedStartQuarter) {
      return false;
    }

    const selectedComparable = this.getQuarterComparableValue(selectedStartQuarter);
    const optionComparable = this.getQuarterComparableValue(option);

    if (selectedComparable < 0 || optionComparable < 0) {
      return false;
    }

    // End quarter must be strictly after start quarter
    return optionComparable <= selectedComparable;
  }

  private getQuarterComparableValue(value: any): number {
    const quarterValues = this.extractQuarterYearValues(value);
    const candidate = String(quarterValues[0] ?? '').trim();
    if (!candidate) {
      return -1;
    }

    // Supports both "Q22025" and "Q2 2025"
    const match = candidate.match(/^Q([1-4])\s*(\d{4})$/i);
    if (!match) {
      return -1;
    }

    const quarter = Number(match[1]);
    const year = Number(match[2]);
    if (!Number.isFinite(quarter) || !Number.isFinite(year)) {
      return -1;
    }

    return year * 10 + quarter;
  }


  public resetSelection(): void {
    if (!this.filters?.content) return;

    this.filters.content.forEach((filter: any) => {
      if (this.isDateRangeFilter(filter)) {
        return;
      }
      if (filter.isMultiSelect) {
        // If currently "All", keep "All", otherwise reset to first value
        const current = this.selectedValues[filter.key];
          if (filter.resetToDefaultOnReset) {
        this.setDefaultSelection(filter, false);
        return;
      }
        if (
          !current ||
          (Array.isArray(current) &&
            current.length === 1 &&
            current[0] === 'All')
        ) {
          this.selectedValues[filter.key] = ['All'];
        } else {
          this.setDefaultSelection(filter, false); // reset to first option
        }
      } else {
        this.setDefaultSelection(filter, false); // single-select reset
      }
    });

    if (this.fromDateFilter && this.toDateFilter) {
      this.setDefaultDateRangeToToday();
    }

    this.emitSelections(); // emit all selections once
  }
  private multiSelectDebounceTimer: any = null;
  handleSelectionChange(filter: any, clickedValue?: any) {
    if (filter.isMultiSelect) {
      const currentSelection = Array.isArray(this.selectedValues[filter.key])
        ? [...this.selectedValues[filter.key]]
        : [];

      // Normalize mixed states from MatSelect event ordering:
      // keep "All" mutually exclusive with concrete selections.
      if (currentSelection.includes('All') && currentSelection.length > 1) {
        this.selectedValues[filter.key] = currentSelection.filter(
          (value: any) => value !== 'All'
        );
      }

      if (clickedValue === undefined) {
        const normalizedSelection = Array.isArray(this.selectedValues[filter.key])
          ? this.selectedValues[filter.key]
          : [];
        if (normalizedSelection.length === 0) {
          this.selectedValues[filter.key] = ['All'];
        }
      }
    }

    if (filter.isMultiSelect && clickedValue !== undefined) {

      // -------------------------
      // CLICKED "ALL"
      // -------------------------
      if (clickedValue === 'All') {
        if (
          this.selectedValues[filter.key]?.includes('All') ||
          this.selectedValues[filter.key]?.length === 0
        ) {
          if (filter.extraKeys?.length) {
            filter.extraKeys.forEach((key: string | number) => {
              this.selectedValues[key] = ['All'];
            });
          }
          this.selectedValues[filter.key] = ['All'];
        } else {
          this.selectedValues[filter.key] = [];
        }
      }

      // -------------------------
      // CLICKED INDIVIDUAL VALUE
      // -------------------------
      else {
        if (filter.extraKeys?.length) {
          filter.extraKeys.forEach((key: string) => {
            if (this.selectedValues[key]?.includes('All')) {
              delete this.selectedValues[key];
            }
          });
        }

        if (this.selectedValues[filter.key]?.includes('All')) {
          this.selectedValues[filter.key] =
            this.selectedValues[filter.key].filter((v: any) => v !== 'All');
        }

        if (
          this.selectedValues[filter.key]?.length === filter.optionsList.length ||
          this.selectedValues[filter.key]?.length === 0
        ) {
          if (filter.extraKeys?.length) {
            filter.extraKeys.forEach((key: string | number) => {
              this.selectedValues[key] = ['All'];
            });
          }
          this.selectedValues[filter.key] = ['All'];
        }
      }
    }

    // -------------------------
    // DEPENDENT FILTER RELOAD (DEBOUNCED)
    // -------------------------
    clearTimeout(this.multiSelectDebounceTimer);
    this.multiSelectDebounceTimer = setTimeout(() => {

      // EXPAND "ALL" HERE
      const parentValueObj = {
        [filter.key]: this.expandAllIfNeeded(filter),
      };

      this.filters.content.forEach((f: any) => {
        if (this.shouldReloadDependentFilter(f, filter.key)) {
          this.loadDependentFilter(f, parentValueObj);
        }
      });

      if (filter.key === 'start_date' || filter.key === 'end_date') {
        this.validateDateRange();
      }
      if (filter.key === 'start_quarter' || filter.key === 'end+quarter') {
        this.validateQuarterRange();
      }

      this.emitSelections();

    }, 200);
  }

  private expandAllIfNeeded(filter: any): any[] {
    const selected = this.selectedValues[filter.key];

    // Not multi-select or no All → normal behavior
    if (!filter.isMultiSelect || !Array.isArray(selected) || !selected.includes('All')) {
      return this.normalizeSelectedValues(selected);
    }

    // Expand "All" → all option values
    return filter.optionsList.map((opt: any) =>
      typeof opt === 'object'
        ? opt.value ?? opt.id ?? opt.name ?? opt
        : opt
    );
  }



  private validateDateRange() {
    const startKey = 'start_date';
    const endKey = 'end_date';

    const startFilter = this.filters.content.find((f: { key: string; }) => f.key === startKey);
    const endFilter = this.filters.content.find((f: { key: string; }) => f.key === endKey);

    const startValue = this.selectedValues[startKey];
    const endValue = this.selectedValues[endKey];

    if (!startValue || !endValue || !startFilter || !endFilter) return;

    const startComparable = this.getComparableValue(startValue, startFilter.valueType || startFilter.type);
    const endComparable = this.getComparableValue(endValue, endFilter.valueType || endFilter.type);

    if (startComparable > endComparable) {
      // Adjust end date only if necessary
      const newEnd = endFilter.optionsList?.find((opt: string) =>
        this.getComparableValue(opt, endFilter.valueType || endFilter.type) >= startComparable
      );

      if (newEnd) {
        this.selectedValues[endKey] = newEnd;
        console.warn('End Date adjusted to be after Start Date');
      }
    }
  }

  private validateQuarterRange(): void {
    const startKey = 'start_quarter';
    const endKey = 'end+quarter';

    const startValue = this.selectedValues[startKey];
    const endValue = this.selectedValues[endKey];
    if (!startValue || !endValue) return;

    const startComparable = this.getQuarterComparableValue(startValue);
    const endComparable = this.getQuarterComparableValue(endValue);
    if (startComparable < 0 || endComparable < 0) return;

    if (endComparable <= startComparable) {
      const endFilter = this.filters?.content?.find((f: any) => f?.key === endKey);
      const options = Array.isArray(endFilter?.optionsList) ? endFilter.optionsList : [];

      const nextValid = options.find((opt: any) => this.getQuarterComparableValue(opt) > startComparable);
      if (nextValid) {
        this.selectedValues[endKey] = nextValid;
      }
    }
  }


  private getComparableValue(value: string, type: string): number {
    if (!value) return -1;

    if (type === 'quarter') {
      // Q12025 → 2025*10 + 1 = 20251 (sortable number)
      const match = value.match(/Q([1-4])(\d{4})/);
      if (match) {
        const quarter = parseInt(match[1], 10);
        const year = parseInt(match[2], 10);
        return year * 10 + quarter;
      }
      return -1;
    } else if (type === 'date') {
      return new Date(value).getTime(); // timestamp for comparison
    } else {
      return parseFloat(value) || -1; // fallback numeric
    }
  }


  private normalizeSelectedValues(selected: any): any[] {
    if (Array.isArray(selected)) {
      if (selected.length === 1 && selected[0] === 'All') {
        return ['All'];
      }
      return selected.map((item) =>
        typeof item === 'object'
          ? Object.keys(item)
            .filter((k) => k !== 'viewValue')
            .map((k) => item[k])[0]
          : item
      );
    } else if (selected && typeof selected === 'object') {
      const { viewValue, ...rest } = selected;
      return Object.values(rest);
    } else {
      return [selected];
    }
  }

  onDropdownOpenedChange(isOpen: boolean, currentDropdown: MatSelect): void {
    if (!this.dropdowns) {
      return;
    }

    if (isOpen) {
      this.dropdowns.forEach((dropdown) => {
        if (dropdown !== currentDropdown && dropdown.panelOpen) {
          dropdown.close();
        }
      });
    } else {
      // Clear in-panel search when the dropdown closes.
      Object.values(this.searchControls).forEach((control) => control.setValue('', { emitEvent: false }));
    }

    // Let Angular Material finish panel open/close transition, then align body class.
    setTimeout(() => {
      const hasOpenDropdown = this.dropdowns?.some((dropdown) => dropdown.panelOpen) ?? false;
      if (hasOpenDropdown) {
        this.removeRoadmapBodyClass();
      } else {
        this.restoreRoadmapBodyClass();
      }
    }, 0);
  }

  getSearchControl(filterKey: string): FormControl<string> {
    if (!this.searchControls[filterKey]) {
      this.searchControls[filterKey] = new FormControl<string>('', { nonNullable: true });
    }
    return this.searchControls[filterKey];
  }

  shouldEnableSearch(filter: any): boolean {
    return filter?.enableSearch !== false;
  }

  getFilteredOptions(filter: any): any[] {
    const options = Array.isArray(filter?.optionsList) ? filter.optionsList : [];
    if (!this.shouldEnableSearch(filter)) {
      return options;
    }

    const searchText = this.getSearchControl(filter.key).value.trim().toLowerCase();
    if (!searchText) {
      return options;
    }

    return options.filter((option: any) => {
      const label = String(option?.viewValue ?? option ?? '')
        .trim()
        .toLowerCase();
      return label.includes(searchText);
    });
  }

  private removeRoadmapBodyClass(): void {
    if (!this.shouldManageRoadmapBodyClass || typeof document === 'undefined') {
      return;
    }
    document.body.classList.remove(this.productRoadmapBodyClass);
  }

  private restoreRoadmapBodyClass(): void {
    if (!this.shouldManageRoadmapBodyClass || typeof document === 'undefined') {
      return;
    }
    document.body.classList.add(this.productRoadmapBodyClass);
  }

  private shouldUseViewValue(viewValue: any, rest: Record<string, any>): boolean {
    if (viewValue === null || viewValue === undefined) return false;
    const restKeys = Object.keys(rest || {});
    if (restKeys.length === 0) return true;
    return restKeys.every((k) => ['value', 'id', 'name'].includes(k));
  }

  private appendOptionObjectValue(
    merged: Record<string, any[]>,
    filterKey: string,
    optionObject: any,
  ): void {
    const { viewValue, ...rest } = optionObject || {};

    if (this.shouldUseViewValue(viewValue, rest)) {
      if (!merged[filterKey]) merged[filterKey] = [];
      merged[filterKey].push(viewValue);
      return;
    }

    Object.keys(rest || {}).forEach((innerKey) => {
      // Prevent one filter's option payload from overriding another configured filter key.
      if (innerKey !== filterKey && this.isConfiguredFilterKey(innerKey)) {
        return;
      }
      if (!merged[innerKey]) merged[innerKey] = [];
      merged[innerKey].push(
        ...(Array.isArray(rest[innerKey]) ? rest[innerKey] : [rest[innerKey]])
      );
    });
  }

  private isConfiguredFilterKey(key: string): boolean {
    return !!this.filters?.content?.some((f: any) => f?.key === key);
  }

  loadDependentFilter(filter: any, parentValue: any) {
    const debugKey = String(filter?.debugKey || '');
    const shouldDebug = !!debugKey;
    if (shouldDebug) {
      console.log(`[${debugKey}] loadDependentFilter:start`, {
        filterKey: filter?.key,
        functionName: filter?.functionName,
        parentValue,
        selectedValues: { ...this.selectedValues },
        dependsOn: filter?.dependsOn,
        requestDataKeys: filter?.requestDataKeys,
        requestDataFilterKeys: filter?.requestDataFilterKeys,
      });
    }

    if (!this.hasResolvedDependencies(filter)) {
      filter.optionsList = [];
      if (shouldDebug) {
        console.log(`[${debugKey}] loadDependentFilter:missingDependencies`, {
          dependencyKeys: this.getDependencyKeys(filter),
          selectedValues: { ...this.selectedValues },
        });
      }
      return;
    }

    const requestFilter = this.buildDependentRequestFilter(filter);
    const shouldForceConfiguredFilter =
      Array.isArray(filter?.requestDataKeys) && filter.requestDataKeys.length > 0;
    const requestConfig = {
      ...filter,
      ...(requestFilter || shouldForceConfiguredFilter
        ? { requestFilter: requestFilter || {} }
        : {}),
    };
    if (shouldDebug) {
      console.log(`[${debugKey}] loadDependentFilter:beforeFetch`, {
        requestFilter,
        shouldForceConfiguredFilter,
        requestConfig,
      });
    }

    this.configService
      .fetchData(
        filter.apiUrl,
        filter.functionName,
        parentValue,
        requestConfig,
        filter.method || 'post'
      )
      .pipe(
        map((res: any) => {
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:response`, res);
          }
          const extractedOptions = this.extractOptionsFromResponse(filter, res);
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:extractedOptions`, {
              respKey: filter?.respKey,
              extractedOptions,
            });
          }
          filter.optionsList = this.sortOptionsForFilter(
            filter,
            extractedOptions,
          );
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:sortedOptions`, {
              optionsList: filter.optionsList,
            });
          }
          return filter;
        }),
        catchError((err) => {
          console.error(`Error fetching ${filter.key} options:`, err);
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:error`, err);
          }
          filter.optionsList = [];
          return of(filter);
        })
      )
      .subscribe((updatedFilter) => {
        const prevSelected = this.selectedValues[updatedFilter.key];
        const forcedDependentDefault =
          updatedFilter?.forceDependentDefaultOnReload === true
            ? this.resolveDependentDefaultSelection(updatedFilter)
            : null;
        if (shouldDebug) {
          console.log(`[${debugKey}] loadDependentFilter:subscribe`, {
            prevSelected,
            optionsList: updatedFilter.optionsList,
          });
        }

        // If it was "All" and is a multi-select → preserve it
        if (forcedDependentDefault != null) {
          this.selectedValues[updatedFilter.key] = forcedDependentDefault;
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:forcedDependentDefaultApplied`, {
              previousSelected: prevSelected,
              selected: this.selectedValues[updatedFilter.key],
            });
          }
          this.emitSelections();
          return;
        }

        if (
          updatedFilter.isMultiSelect &&
          Array.isArray(prevSelected) &&
          prevSelected.includes('All')
        ) {
          this.selectedValues[updatedFilter.key] = ['All'];
          this.emitSelections();
          return;
        }

        // Build a list of comparable option values (normalize objects to primitive keys)
        const optionValues = updatedFilter.optionsList.map((opt: any) =>
          typeof opt === 'object'
            ? opt.value ?? opt.id ?? opt.name ?? opt
            : opt
        );

        let stillValid: any[] = [];

        if (Array.isArray(prevSelected)) {
          // MULTI select
          stillValid = prevSelected.filter((val: any) =>
            optionValues.includes(
              typeof val === 'object'
                ? val.value ?? val.id ?? val.name ?? val
                : val
            )
          );
        } else if (prevSelected != null) {
          // SINGLE select
          const normalizedPrev =
            typeof prevSelected === 'object'
              ? prevSelected.value ??
              prevSelected.id ??
              prevSelected.name ??
              prevSelected
              : prevSelected;

          if (optionValues.includes(normalizedPrev)) {
            stillValid = [prevSelected];
          }
        }

        if (stillValid.length > 0) {
          // Re-apply valid previous selection(s)
          this.selectedValues[updatedFilter.key] =
            updatedFilter.isMultiSelect ? stillValid : stillValid[0];
          if (shouldDebug) {
            console.log(`[${debugKey}] loadDependentFilter:preservedSelection`, {
              selected: this.selectedValues[updatedFilter.key],
            });
          }
          this.emitSelections();
        } else {
          const dependentDefault = this.resolveDependentDefaultSelection(updatedFilter);
          if (dependentDefault != null) {
            this.selectedValues[updatedFilter.key] = dependentDefault;
            if (shouldDebug) {
              console.log(`[${debugKey}] loadDependentFilter:dependentDefaultApplied`, {
                selected: this.selectedValues[updatedFilter.key],
              });
            }
            this.emitSelections();
          } else {
            // Fallback to default logic
            if (shouldDebug) {
              console.log(`[${debugKey}] loadDependentFilter:fallbackDefault`);
            }
            this.setDefaultSelection(updatedFilter);
          }
        }
      });
  }

  private resolveDependentDefaultSelection(filter: any): any | null {
    if (
      filter?.defaultFromDependency !== 'sameQuarterNextYear' ||
      !Array.isArray(filter?.optionsList) ||
      !filter.optionsList.length
    ) {
      return null;
    }

    const sourceKey = String(filter?.defaultDependencyKey || 'start_date');
    const sourceValue = this.selectedValues[sourceKey];
    const sourceQuarter = this.parseQuarterYear(sourceValue);
    if (!sourceQuarter) {
      return null;
    }

    const targetValue = `Q${sourceQuarter.quarter}${sourceQuarter.year + 1}`;
    const normalizedTarget = this.getOptionComparableValue(targetValue, filter?.key);
    const match = filter.optionsList.find(
      (option: any) => this.getOptionComparableValue(option, filter?.key) === normalizedTarget
    ) ?? null;

    if (filter?.debugKey) {
      console.log(`[${filter.debugKey}] resolveDependentDefaultSelection`, {
        sourceKey,
        sourceValue,
        sourceQuarter,
        targetValue,
        normalizedTarget,
        match,
        optionsList: filter.optionsList,
      });
    }

    return match;
  }

  private parseQuarterYear(value: any): { quarter: number; year: number } | null {
    const rawValue = Array.isArray(value) ? value[0] : value;
    const candidate =
      rawValue && typeof rawValue === 'object'
        ? rawValue.value ?? rawValue.id ?? rawValue.name ?? rawValue.viewValue
        : rawValue;
    const match = String(candidate ?? '').trim().match(/^Q([1-4])(\d{4})$/i);
    if (!match) {
      return null;
    }

    return {
      quarter: Number(match[1]),
      year: Number(match[2]),
    };
  }

  emitSelections() {
    const formattedSelections: any = {};
    const combineQuarterRange = this.shouldCombineQuarterYearRange();
    const combineYearMonthConfig = this.getYearMonthCombineConfig();

    if (combineQuarterRange) {
      const combinedQuarterValues = [
        ...this.extractQuarterYearValues(this.selectedValues['start_quarter']),
        ...this.extractQuarterYearValues(this.selectedValues['end+quarter']),
      ];

      const uniqueQuarterValues = Array.from(
        new Set(combinedQuarterValues.map((value) => String(value).trim()).filter((value) => value !== ''))
      );

      if (uniqueQuarterValues.length > 0) {
        formattedSelections['quarter_year'] = uniqueQuarterValues;
      }
    }

    Object.keys(this.selectedValues).forEach((key) => {
      if (combineQuarterRange && (key === 'start_quarter' || key === 'end+quarter')) {
        return;
      }

      if (
        combineYearMonthConfig &&
        (key === combineYearMonthConfig.yearKey || key === combineYearMonthConfig.monthKey)
      ) {
        return;
      }

      const value = this.selectedValues[key];
      const configuredFilter = this.filters.content.find(
        (filter: any) => filter.key === key
      );

      if (
        configuredFilter?.omitAllSelection === true &&
        (value === 'All' ||
          (Array.isArray(value) && value.length === 1 && value[0] === 'All'))
      ) {
        formattedSelections[key] = [];
        return;
      }

      // -------------------------------
      // CASE 1: ["All"] → emit ALL values
      // -------------------------------
      if (Array.isArray(value) && value.length === 1 && value[0] === 'All') {
        const filter = this.filters.content.find((f: any) => f.key === key);
        if (!filter) return;

        const merged: any = {};

        filter.optionsList?.forEach((opt: any) => {
          if (opt && typeof opt === 'object') {
            this.appendOptionObjectValue(merged, key, opt);
          } else {
            if (!merged[key]) merged[key] = [];
            merged[key].push(opt);
          }
        });

        Object.keys(merged).forEach((k) => {
          this.setFormattedSelectionValue(
            formattedSelections,
            k,
            Array.from(new Set(merged[k]))
          );
        });

        return;
      }

      // -------------------------------
      // CASE 2: MULTI-SELECT
      // -------------------------------
      if (Array.isArray(value)) {
        const merged: any = {};

        value.forEach((item) => {
          if (item && typeof item === 'object') {
            this.appendOptionObjectValue(merged, key, item);
          } else {
            if (!merged[key]) merged[key] = [];
            merged[key].push(item);
          }
        });

        Object.keys(merged).forEach((k) => {
          this.setFormattedSelectionValue(
            formattedSelections,
            k,
            Array.from(new Set(merged[k]))
          );
        });

        return;
      }

	// -------------------------------
	// CASE 3: SINGLE-SELECT OBJECT
	// -------------------------------
	if (value && typeof value === 'object') {
	  const { viewValue, ...rest } = value;

	  if (this.shouldUseViewValue(viewValue, rest)) {
	    formattedSelections[key] = [viewValue];
	  } else if (Object.keys(rest).length > 0) {
	    Object.keys(rest).forEach((innerKey) => {

	      if (innerKey !== key && this.isConfiguredFilterKey(innerKey)) {
	        return;
	      }

	      const selectionValues = Array.isArray(rest[innerKey])
	        ? rest[innerKey]
	        : [rest[innerKey]];

	      formattedSelections[innerKey] = selectionValues;

	      this.setFormattedSelectionValue(
	        formattedSelections,
	        innerKey,
	        selectionValues
	      );
	    });
	  } else if (viewValue != null) {
	    this.setFormattedSelectionValue(formattedSelections, key, [viewValue]);
	  }

	  return;
	}

      // -------------------------------
      // CASE 4: SINGLE-SELECT PRIMITIVE
      // -------------------------------
      if (value != null) {
        this.setFormattedSelectionValue(formattedSelections, key, [value]);
      }
    });

    if (combineYearMonthConfig) {
      const yearValue = this.unwrapYearMonthSelection(this.selectedValues[combineYearMonthConfig.yearKey]);
      const monthValue = this.unwrapYearMonthSelection(this.selectedValues[combineYearMonthConfig.monthKey]);
      const targetKey = String(combineYearMonthConfig.targetKey || '').trim();

      if (
        targetKey &&
        yearValue &&
        monthValue &&
        yearValue !== 'All' &&
        monthValue !== 'All'
      ) {
        formattedSelections[targetKey] = [`${yearValue}-${String(monthValue).padStart(2, '0')}`];
      }
    }

    this.filterState.updateFilterSelection(formattedSelections, '');
    this.filtersChanged.emit(formattedSelections);
  }

  private unwrapYearMonthSelection(selection: any): string {
    const value = Array.isArray(selection) ? selection[0] : selection;

    if (value === null || value === undefined || value === '') {
      return '';
    }

    if (typeof value === 'object') {
      const candidate = value.value ?? value.id ?? value.name ?? value.viewValue;
      return String(candidate ?? '').trim();
    }

    return String(value).trim();
  }


}
